import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, PlugZap, Save } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { Panel } from "./shared";

const WEBHOOK_URL = `${process.env.REACT_APP_BACKEND_URL}/api/webhooks/shopify/orders`;
const inputCls = "h-12 w-full px-3 bg-[#090D16] border border-[#334155] rounded-md focus:outline-none focus:border-[#F59E0B] font-mono text-sm";

const Field = ({ label, hint, children }) => (
  <label className="flex flex-col gap-1">
    <span className="font-mono text-[10px] tracking-[0.2em] text-[#6B7280]">{label}</span>
    {children}
    {hint && <span className="text-xs text-[#6B7280]">{hint}</span>}
  </label>
);

export default function ShopifySettings() {
  const [s, setS] = useState(null);
  const [f, setF] = useState({ shop_domain: "", access_token: "", webhook_secret: "", api_version: "", sync_enabled: true });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await api.get("/shopify/settings");
    setS(r.data);
    setF({ shop_domain: r.data.shop_domain, access_token: "", webhook_secret: "", api_version: r.data.api_version, sync_enabled: r.data.sync_enabled });
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try { await api.put("/shopify/settings", f); toast.success("Shopify settings saved"); await load(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const clear = async (key) => {
    if (!window.confirm("Remove the saved value?")) return;
    try { await api.put("/shopify/settings", { ...f, access_token: "", webhook_secret: "", [key]: true }); toast.success("Removed"); await load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const ClearBtn = ({ k, testId }) => (
    <button type="button" data-testid={testId} onClick={() => clear(k)} className="h-12 px-3 shrink-0 rounded-md border border-[#EF4444]/40 text-[#EF4444] text-xs font-bold">CLEAR</button>
  );
  const test = async () => {
    setBusy(true);
    try { const r = await api.post("/shopify/test-connection"); toast.success(`Connected to ${r.data.shop} · location: ${r.data.location}`); await load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!s) return <div className="font-mono text-[#6B7280]">LOADING…</div>;

  return (
    <div className="grid xl:grid-cols-[1fr_420px] gap-6">
      <Panel className="p-6 space-y-5" data-testid="shopify-settings-form">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-bold">Store Connection</h2>
          <span data-testid="shopify-status-badge" className={`font-mono text-[10px] font-bold px-2 py-1 rounded-sm ${s.configured ? "bg-[#10B981]/20 text-[#10B981]" : "bg-[#F59E0B]/20 text-[#F59E0B]"}`}>
            {s.configured ? "CONFIGURED" : "NOT CONFIGURED"}
          </span>
        </div>
        <Field label="STORE DOMAIN" hint="e.g. your-store.myshopify.com">
          <input data-testid="shopify-domain-input" className={inputCls} value={f.shop_domain} onChange={(e) => setF({ ...f, shop_domain: e.target.value })} placeholder="your-store.myshopify.com" />
        </Field>
        <Field label="ADMIN API ACCESS TOKEN" hint={s.has_access_token ? `Saved (${s.access_token_masked}). Leave blank to keep.` : "Starts with shpat_"}>
          <div className="flex gap-2">
            <input data-testid="shopify-token-input" type="password" className={inputCls} value={f.access_token} onChange={(e) => setF({ ...f, access_token: e.target.value })} placeholder={s.has_access_token ? s.access_token_masked : "shpat_…"} />
            {s.has_access_token && <ClearBtn k="clear_access_token" testId="shopify-clear-token-btn" />}
          </div>
        </Field>
        <Field label="WEBHOOK SIGNING SECRET" hint={s.has_webhook_secret ? `Saved (${s.webhook_secret_masked}). Leave blank to keep.` : "Used to verify X-Shopify-Hmac-Sha256"}>
          <div className="flex gap-2">
            <input data-testid="shopify-secret-input" type="password" className={inputCls} value={f.webhook_secret} onChange={(e) => setF({ ...f, webhook_secret: e.target.value })} placeholder={s.has_webhook_secret ? s.webhook_secret_masked : "secret"} />
            {s.has_webhook_secret && <ClearBtn k="clear_webhook_secret" testId="shopify-clear-secret-btn" />}
          </div>
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="API VERSION"><input data-testid="shopify-version-input" className={inputCls} value={f.api_version} onChange={(e) => setF({ ...f, api_version: e.target.value })} /></Field>
          <Field label="PRIMARY LOCATION"><div data-testid="shopify-location" className="h-12 flex items-center px-3 border border-[#1E293B] rounded-md text-sm text-[#9CA3AF]">{s.location_name || "Detected on first sync / test"}</div></Field>
        </div>
        <label className="flex items-center gap-3 h-12 cursor-pointer">
          <input data-testid="shopify-sync-toggle" type="checkbox" checked={f.sync_enabled} onChange={(e) => setF({ ...f, sync_enabled: e.target.checked })} className="w-5 h-5 accent-[#F59E0B]" />
          <span className="text-sm">Push inventory counts back to Shopify after each order</span>
        </label>
        <div className="flex flex-wrap gap-3">
          <button data-testid="shopify-save-btn" disabled={busy} onClick={save} className="h-12 px-5 rounded-md bg-[#F59E0B] text-[#090D16] font-bold flex items-center gap-2 disabled:opacity-40"><Save className="w-4 h-4" />Save</button>
          <button data-testid="shopify-test-btn" disabled={busy || !s.configured} onClick={test} className="h-12 px-5 rounded-md border border-[#334155] font-semibold flex items-center gap-2 disabled:opacity-40"><PlugZap className="w-4 h-4" />Test connection</button>
        </div>
      </Panel>
      <Panel className="p-6 space-y-4 text-sm text-[#9CA3AF]">
        <h3 className="font-display text-lg font-bold text-white">Shopify setup</h3>
        <Field label="WEBHOOK URL (same URL for every topic below, JSON)">
          <div className="flex gap-2">
            <input readOnly data-testid="shopify-webhook-url" className={`${inputCls} text-[#38BDF8]`} value={WEBHOOK_URL} />
            <button data-testid="shopify-copy-url-btn" onClick={() => { navigator.clipboard?.writeText(WEBHOOK_URL); toast.success("Copied"); }} className="h-12 w-12 shrink-0 flex items-center justify-center border border-[#334155] rounded-md"><Copy className="w-4 h-4" /></button>
          </div>
        </Field>
        <ol className="list-decimal pl-5 space-y-2">
          <li>Shopify Admin → Settings → Apps and sales channels → Develop apps → Create app.</li>
          <li>Admin API scopes: <span className="font-mono text-xs text-white">read_products, read_inventory, write_inventory, read_locations, read_orders</span>. Install and copy the access token.</li>
          <li>Settings → Notifications → Webhooks → Create webhook: event <span className="text-white">Order payment</span> (orders/paid), format JSON, URL above. Copy the signing secret shown on that page. Paid orders deduct stock; bundle SKUs deduct every component or nothing.</li>
          <li data-testid="shopify-setup-cancel-step">Create a second webhook with the same URL: event <span className="text-white">Order cancellation</span> (orders/cancelled), format JSON. The stock that order deducted is put back.</li>
          <li data-testid="shopify-setup-refund-step">Create a third webhook with the same URL: event <span className="text-white">Refund create</span> (refunds/create), format JSON. Returns are processed through refunds: only the refunded quantities are put back, and lines refunded with “no restock” are logged without a stock change.</li>
          <li>All three webhooks share the signing secret above. Each event appears in the Webhook Log (RESTOCKED for cancellations and refunds).</li>
          <li>Shopify variant SKUs must match the Tier 3 item SKUs here, or a bundle's Shopify SKU (Items → Kits / Bundles).</li>
        </ol>
      </Panel>
    </div>
  );
}
